import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { ApiService } from './api.service';
import { ConfigurationManagerService } from './configuration-manager.service';

export interface VehicleMonitorRegistration {
  trackerId: string;
  vehicleNo: string;
  make?: string;
  model?: string;
  color?: string;
  vehicleType?: string;
  driverName?: string;
  licenseNo?: string;
  status?: string;
}

export interface VehicleMonitorVehicle {
  trackerId: string;
  vehicleNo: string;
  latitude: number;
  longitude: number;
  speedKph: number;
  heading: number;
  recordedAt: string | null;
  sequence: number;
  roadName?: string | null;
  motionPath?: { latitude: number; longitude: number }[] | null;
  registration: VehicleMonitorRegistration | null;
  authorized: boolean;
  zone: 'OUTSIDE' | 'INSIDE' | 'WARNING' | 'CRITICAL';
  distanceMeters: number | null;
  siteId: string | null;
  homeCampusKey?: string;
  campusId?: string | null;
  campusLabel?: string | null;
}

export interface VehicleCampusGroup {
  id: string;
  label: string;
  total: number;
  inside: number;
  outside: number;
  authorisedInside: number;
  authorisedOutside: number;
  unauthorisedInside: number;
  unauthorisedOutside: number;
  entering: number;
  exiting: number;
}

export interface VehicleCampusFence {
  id: string;
  label: string;
  type?: string;
  geometry?: { type?: string; coordinates?: unknown };
}

export interface VehicleMonitorGeofence {
  siteId: string | null;
  siteName: string;
  latitude: number;
  longitude: number;
  radiusMeters: number;
  warningRadiusMeters: number;
  criticalRadiusMeters: number;
  active: boolean;
}

export interface VehicleMonitorAlert {
  trackerId: string;
  vehicleNo: string;
  zone: VehicleMonitorVehicle['zone'];
  distanceMeters: number | null;
  siteId: string | null;
  campusId?: string | null;
  campusLabel?: string | null;
  latitude?: number;
  longitude?: number;
  message: string;
  recordedAt?: string | null;
}

export interface VehicleMonitorSimulation {
  state: 'STOPPED' | 'RUNNING' | 'PAUSED' | 'COMPLETED';
  speed: number;
  step: number;
  totalSteps: number;
  intervalMs: number;
  tickMs?: number;
  loop: boolean;
  cycle: number;
  elapsedSeconds: number;
  durationSeconds: number;
  remainingSeconds: number;
}

export interface VehicleMonitorSnapshot {
  id: string;
  site: { siteId: string; siteName: string; latitude: number; longitude: number } | null;
  config: {
    simulationIntervalMs: number;
    loopSimulation: boolean;
    registeredMarkerColor: string;
    unknownMarkerColor: string;
    showVehicleLabels: boolean;
  };
  vehicles: VehicleMonitorVehicle[];
  campuses?: VehicleCampusFence[];
  geofences: VehicleMonitorGeofence[];
  alerts: VehicleMonitorAlert[];
  alertEvents: VehicleMonitorAlert[];
  simulation: VehicleMonitorSimulation;
  counts?: {
    total: number;
    inside: number;
    outside: number;
    authorised?: number;
    unauthorised?: number;
    entering: number;
    exiting: number;
    moving: number;
    slow: number;
    byCampus?: VehicleCampusGroup[];
  };
}

interface ApiEnvelope<T> {
  success: boolean;
  data: T;
  message: string;
}

@Injectable({ providedIn: 'root' })
export class VehicleMonitorService {
  private readonly api = inject(ApiService);
  private readonly vehicleViewOnSubject = new BehaviorSubject(false);
  private readonly latestSnapshotSubject = new BehaviorSubject<VehicleMonitorSnapshot | null>(null);
  private readonly selectedCampusIdSubject = new BehaviorSubject<string | null>(null);
  private readonly postedGeofenceAlerts = new Set<string>();
  readonly vehicleViewOn$ = this.vehicleViewOnSubject.asObservable();
  readonly latestSnapshot$ = this.latestSnapshotSubject.asObservable();
  readonly selectedCampusId$ = this.selectedCampusIdSubject.asObservable();

  isVehicleViewOn(): boolean {
    return this.vehicleViewOnSubject.value;
  }

  setVehicleViewOn(on: boolean): void {
    this.vehicleViewOnSubject.next(on);
    if (!on) {
      this.selectedCampusIdSubject.next(null);
    }
  }

  selectedCampusId(): string | null {
    return this.selectedCampusIdSubject.value;
  }

  selectCampus(id: string | null): void {
    this.selectedCampusIdSubject.next(id);
  }

  setLatestSnapshot(snapshot: VehicleMonitorSnapshot | null): void {
    this.latestSnapshotSubject.next(snapshot);
  }

  latestSnapshot(): VehicleMonitorSnapshot | null {
    return this.latestSnapshotSubject.value;
  }

  private base(): string {
    const settings = ConfigurationManagerService.settings;
    const prefix = (settings.VehicleMonitor || 'vehicle-monitor/').replace(/^\//, '');
    const host = (settings.VehicleMonitorBaseURL || settings.BaseURL || '').replace(/\/?$/, '/');
    return `${host}${prefix}`;
  }

  setIllumGeofences(geofences: unknown[]): Observable<ApiEnvelope<{ accepted: number }>> {
    return this.api.post<ApiEnvelope<{ accepted: number }>>(`${this.base()}illum-geofence`, { geofences });
  }

  snapshot(): Observable<ApiEnvelope<VehicleMonitorSnapshot>> {
    return this.api.get<ApiEnvelope<VehicleMonitorSnapshot>>(`${this.base()}snapshot`);
  }

  status(): Observable<ApiEnvelope<VehicleMonitorSimulation>> {
    return this.api.get<ApiEnvelope<VehicleMonitorSimulation>>(`${this.base()}status`);
  }

  command(action: string, speed?: number): Observable<ApiEnvelope<VehicleMonitorSimulation | VehicleMonitorSnapshot>> {
    const body = speed === undefined ? {} : { speed };
    return this.api.post<ApiEnvelope<VehicleMonitorSimulation | VehicleMonitorSnapshot>>(`${this.base()}${action}`, body);
  }

  publishUnauthorizedEntryAlerts(snapshot: VehicleMonitorSnapshot | null): void {
    if (!snapshot?.alertEvents?.length) {
      return;
    }
    for (const event of snapshot.alertEvents) {
      const key = String(event.trackerId || '');
      if (!key || this.postedGeofenceAlerts.has(key)) {
        continue;
      }
      this.postedGeofenceAlerts.add(key);
      this.postCampusEntryAlert(event).subscribe({
        error: () => {
          this.postedGeofenceAlerts.delete(key);
        }
      });
    }
  }

  clearPostedGeofenceAlerts(): void {
    this.postedGeofenceAlerts.clear();
  }

  private postCampusEntryAlert(event: VehicleMonitorAlert): Observable<unknown> {
    const settings = ConfigurationManagerService.settings;
    const url = `${(settings.BaseURL || '').replace(/\/?$/, '/')}${String(settings.AlertDismiss || 'db/Alert/').replace(/\/?$/, '')}`;
    const orgRaw = (typeof localStorage !== 'undefined' ? localStorage.getItem('PAA_org') : '') || '';
    const organizationId = Number(orgRaw.replace(/["']/g, '').trim());
    const campusName = event.campusLabel || 'campus';
    const vehicleLabel = event.vehicleNo || event.trackerId;
    const lon = typeof event.longitude === 'number' ? event.longitude : 0;
    const lat = typeof event.latitude === 'number' ? event.latitude : 0;
    const feature = {
      geometry: {
        type: 'Point',
        coordinates: [lon, lat]
      },
      properties: {
        name: 'unauthorised vehicle',
        trackerId: event.trackerId,
        vehicleNo: vehicleLabel,
        campus: campusName
      }
    };
    const body = {
      organizationId: Number.isFinite(organizationId) ? organizationId : 2025,
      organizationIncidentCategoryId: 1304,
      name: `Unauthorised vehicle No. ${vehicleLabel} has entered ${campusName}`,
      userId: null,
      sensorId: null,
      observationId: null,
      occuredAt: new Date().toISOString(),
      expiresAt: 'Infinity',
      viewedAt: 'Infinity',
      dismissedAt: 'Infinity',
      features: [feature]
    };
    const base = (settings.BaseURL || '').replace(/\/?$/, '/');
    return this.api.post<Record<string, unknown>>(url, body).pipe(
      switchMap((created) => {
        const alertId = Number((created as { id?: unknown })?.id
          ?? (created as { data?: { id?: unknown } })?.data?.id);
        const hasPoint = Number.isFinite(lon) && Number.isFinite(lat) && !(lon === 0 && lat === 0);
        if (!Number.isFinite(alertId) || !hasPoint) {
          return of(created);
        }
        return this.api.post(`${base}db/AlertFeature`, {
          alertId,
          geometry: feature.geometry,
          properties: feature.properties
        }).pipe(
          switchMap(() => of(created)),
          catchError(() => of(created))
        );
      })
    );
  }

  /** Wall-clock between GPS samples. Never divide interval by speed — that makes cars blink. */
  playbackTickMs(simulation: { intervalMs?: number; tickMs?: number } | undefined): number {
    if (typeof simulation?.tickMs === 'number' && simulation.tickMs > 0) {
      return simulation.tickMs;
    }
    return simulation?.intervalMs || 1000;
  }
}
